import type { ColDef, IServerSideDatasource } from "ag-grid-community";
import React from "react";

import { type RouterInputs, trpc } from "@blis/trpc/react";

import { Badge } from "@/components/ui/badge";
import { buttonVariants } from "@/components/ui/button";
import { Link } from "@/components/ui/link";
import {
  NonFocusableTooltipTarget,
  Tooltip,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  actionsColumn,
  dateColumn,
  orgColumn,
  priceColumn,
} from "@/features/admin/utils/columns";
import { formatCurrencyValue } from "@/features/admin/utils/currency";
import { useAdminServerSideGrid } from "@/features/admin/utils/useAdminServerSideGrid";
import { AdminGrid, denseAdminGridTheme } from "../AdminGrid";
import { TableFilterRow } from "../TableFilterRow";
import {
  getPendingInvoiceTotal,
  type InvoiceTableRow as Row,
  toInvoiceAdminFilterModel,
  toInvoiceTableRows,
} from "./invoiceTableRows";

const PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 300;

type InvoiceFilterModel =
  RouterInputs["admin"]["billing"]["invoices"]["filterModel"];

export function InvoicesTable() {
  const utils = trpc.useUtils();
  const [pendingTotal, setPendingTotal] = React.useState(0);
  const [searchText, setSearchText] = React.useState("");
  const [debouncedSearch, setDebouncedSearch] = React.useState("");

  React.useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedSearch(searchText);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [searchText]);

  const dataSource = useInvoicesDataSource(debouncedSearch, setPendingTotal);
  const { gridProps } = useAdminServerSideGrid<Row>({
    serverSideDatasource: dataSource,
    paginationPageSize: PAGE_SIZE,
    refreshDeps: [debouncedSearch],
  });
  const filterTextProps = React.useMemo(
    () => ({
      "aria-label": "Table search",
      value: searchText,
      onChange: setSearchText,
      placeholder: "Search...",
    }),
    [searchText],
  );

  const columnDefs = React.useMemo<ColDef<Row>[]>(
    () => [
      {
        colId: "invoice",
        headerName: "Invoice",
        maxWidth: 120,
        minWidth: 100,
        sortable: false,
        cellRenderer: ({ data }: { data?: Row }) => {
          if (!data) return null;
          if (data.status === "completed" && data.invoiceId !== null) {
            return (
              <Badge variant="outline" color="gray" size="sm" className="w-14">
                {data.invoiceId}
              </Badge>
            );
          }
          return (
            <Badge variant="soft" color="amber" size="sm">
              Pending
            </Badge>
          );
        },
      },
      {
        ...dateColumn,
        colId: "createdAt",
        headerName: "Date",
        maxWidth: 140,
        sortable: false,
        valueGetter: ({ data }) => data?.createdAt ?? null,
      },
      {
        ...orgColumn,
        field: "orgName",
        headerName: "Org",
        minWidth: 200,
        filterParams: {
          values: async (params) => {
            try {
              const values = await utils.admin.billing.invoiceOrgNames.fetch();
              params.success(values);
            } catch (_err) {
              params.fail();
            }
          },
        },
      },
      {
        colId: "status",
        headerName: "Status",
        maxWidth: 140,
        minWidth: 110,
        filter: "agSetColumnFilter",
        filterParams: { values: ["Pending", "Completed"] },
        valueGetter: ({ data }) =>
          data?.status === "completed" ? "Completed" : "Pending",
      },
      {
        ...priceColumn,
        field: "total",
        headerName: "Total",
        filter: false,
        sortable: false,
      },
      {
        ...actionsColumn,
        colId: "actions",
        headerName: "",
        cellRenderer: ({ data }: { data?: Row }) => {
          if (!data) return null;
          const href =
            data.status === "completed" && data.invoiceId !== null
              ? ({
                  to: "/admin/billing/invoices/$id",
                  params: { id: data.invoiceId },
                } as const)
              : ({
                  to: "/admin/billing/invoices/preview/$orgId",
                  params: { orgId: data.orgId },
                } as const);
          return (
            <div className="flex h-full items-center justify-end pe-2">
              <Link
                href={href}
                className={buttonVariants({ variant: "outline", size: "sm" })}
              >
                View
              </Link>
            </div>
          );
        },
      },
    ],
    [utils],
  );

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <TableFilterRow inputProps={filterTextProps}>
        <TooltipTrigger delay={300}>
          <NonFocusableTooltipTarget>
            <span className="flex items-center gap-2 shrink-0 cursor-default">
              <span className="text-sm text-muted-foreground underline decoration-dotted decoration-muted-foreground/50 underline-offset-2">
                Pending total:
              </span>
              <Badge
                color="amber"
                variant="soft"
                size="md"
                className="font-semibold tabular-nums"
              >
                {formatCurrencyValue(pendingTotal)}
              </Badge>
            </span>
          </NonFocusableTooltipTarget>
          <Tooltip>
            Sum of pending line items for orgs that match the current search and
            org filters. Date filters only change which rows appear in the grid;
            this total still includes pending that matches search and org
            filters.
          </Tooltip>
        </TooltipTrigger>
      </TableFilterRow>
      <div className="flex flex-col flex-1 min-h-0 border rounded-lg overflow-hidden">
        <AdminGrid<Row>
          {...gridProps}
          columnDefs={columnDefs}
          theme={denseAdminGridTheme}
          getRowId={({ data }) => data.id}
          defaultColDef={{
            flex: 1,
            resizable: true,
            sortable: false,
            filterParams: { maxNumConditions: 1 },
          }}
          suppressDragLeaveHidesColumns
        />
      </div>
    </div>
  );
}

function useInvoicesDataSource(
  debouncedSearch: string,
  setPendingTotal: React.Dispatch<React.SetStateAction<number>>,
): IServerSideDatasource {
  const utils = trpc.useUtils();

  return React.useMemo(
    () => ({
      getRows: async (params) => {
        const { startRow, endRow, filterModel } = params.request;

        try {
          const data = await utils.admin.billing.invoices.fetch({
            take: (endRow ?? 0) - (startRow ?? 0),
            skip: startRow ?? 0,
            search: debouncedSearch,
            filterModel:
              toInvoiceAdminFilterModel<InvoiceFilterModel>(filterModel),
          });

          setPendingTotal(getPendingInvoiceTotal(data));
          params.success({
            rowData: toInvoiceTableRows(data.data),
            rowCount: data.totalCount,
          });
        } catch (_err) {
          params.fail();
        }
      },
    }),
    [debouncedSearch, setPendingTotal, utils],
  );
}
